import { useState } from 'react';
import { PageHeader, Spinner, ErrorBanner } from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { MetricDefinitionEditor } from '@/components/admin/MetricDefinitionEditor';
import { FamilyMaturitySection } from '@/components/admin/FamilyMaturitySection';
import { TabAnalyzerPromptsEditor } from '@/components/admin/TabAnalyzerPromptsEditor';
import { useMetricDefinitions } from '@/hooks/use-metric-definition';
import { cn } from '@/lib/utils';
import type { MetricGroupKey } from '@skystone/shared';

const GROUP_ICON: Record<MetricGroupKey, string> = {
  engineering: '🚀',
  product: '📦',
  code_quality: '🛡️',
  governance: '🧭',
};

type AdminTab = 'definitions' | 'ai_analyzers';

const TAB_ITEMS: ReadonlyArray<{ id: AdminTab; label: string }> = [
  { id: 'definitions', label: 'Metric Definitions' },
  { id: 'ai_analyzers', label: 'AI Analyzers' },
];

export function AdminMetricDefinitionsPage() {
  const { groups, isLoading, error } = useMetricDefinitions();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('definitions');

  function toggleMetric(metricKey: string) {
    setExpandedKey((prev) => (prev === metricKey ? null : metricKey));
  }

  if (isLoading) {
    return (
      <PageContainer variant="narrow" className="space-y-6 p-4">
        <PageHeader title="Metric Definitions & Thresholds" subtitle="Edit what users see on the Definitions Catalogue page and the dashboards." />
        <div className="flex items-center justify-center py-12"><Spinner /></div>
      </PageContainer>
    );
  }

  return (
    <PageContainer variant="narrow" className="space-y-6 p-4">
      <PageHeader
        title="Metric Definitions & Thresholds"
        subtitle="Edit what users see on the Definitions Catalogue page and the dashboards."
      />

      {error && <ErrorBanner message={error} />}

      <div role="tablist" aria-label="Admin sections" className="flex gap-1 border-b border-border">
        {TAB_ITEMS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            data-testid={`admin-tab-${tab.id}`}
            className={cn(
              'border-b-2 px-4 py-2 text-sm font-semibold',
              activeTab === tab.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'definitions' ? (
        <>
          {groups.map((group) => (
            <section key={group.key} className="space-y-3">
              <h2 className="text-sm font-bold text-foreground">
                {GROUP_ICON[group.key]} {group.label}
              </h2>
              <div className="space-y-2">
                {group.metrics.map((metric) => (
                  <MetricDefinitionEditor
                    key={metric.metricKey}
                    definition={metric}
                    isExpanded={expandedKey === metric.metricKey}
                    onToggle={() => toggleMetric(metric.metricKey)}
                  />
                ))}
              </div>
            </section>
          ))}

          <FamilyMaturitySection />
        </>
      ) : (
        <TabAnalyzerPromptsEditor />
      )}
    </PageContainer>
  );
}
